import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { casdoor } from "@/lib/auth/casdoor";
import { enroll, type AuthError, type EnrollInput } from "@/lib/authv2/api";
import { getFingerprint, getDeviceLabel } from "@/lib/authv2/fingerprint";
import { goHomeAfterLogin } from "@/lib/authv2/navigation";
import { markSessionSnapshotFresh } from "@/lib/authv2/refreshInterceptor";
import { STAFF_INACTIVE_CODE } from "@/lib/authv2/staffInactive";
import { useAuthV2 } from "@/lib/authv2/store";
import { PinPad } from "@/components/PinPad";

export const Route = createFileRoute("/auth/callback")({
  component: CallbackPage,
});

function CallbackPage() {
  const exchanged = useRef(false);

  // Tokens captured from Casdoor are kept locally only — never written to
  // localStorage. They flow directly into /auth/enroll once the user types a PIN.
  const [tokens, setTokens] = useState<{ access: string; refresh: string; refreshExpiresIn?: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Step 1: exchange code → tokens. Strict-mode + refresh both replay the
  // single-use code; the ref guard makes that a no-op.
  useEffect(() => {
    if (exchanged.current) return;
    exchanged.current = true;
    (async () => {
      try {
        const code = new URL(window.location.href).searchParams.get("code");
        if (!code) {
          setError("Thiếu code từ Casdoor");
          return;
        }
        const resp = await exchangeCodeForCasdoorTokenWithRefresh();
        window.history.replaceState({}, "", "/auth/callback");
        const nextTokens: { access: string; refresh: string; refreshExpiresIn?: number } = {
          access: resp.access_token,
          refresh: resp.refresh_token,
        };
        if (typeof resp.refresh_expires_in === "number" && resp.refresh_expires_in > 0) {
          nextTokens.refreshExpiresIn = resp.refresh_expires_in;
        }
        setTokens(nextTokens);
      } catch (e) {
        setError(`OAuth thất bại: ${(e as Error).message}`);
      }
    })();
  }, []);

  // Step 2: nhân viên nhập PIN → /auth/enroll. Chạy thẳng trong handler của
  // PinPad, không qua effect: enroll là hệ quả của thao tác người dùng, và
  // effect từng chạy lại mỗi khi `tokens`/`navigate` đổi identity.
  const submitPin = (nextPin: string) => {
    if (!tokens || busy) return;
    setBusy(true);
    const input: EnrollInput = {
      access_token: tokens.access,
      refresh_token: tokens.refresh,
      fingerprint: getFingerprint(),
      pin: nextPin,
      label: getDeviceLabel(),
      user_agent: navigator.userAgent,
    };
    if (typeof tokens.refreshExpiresIn === "number" && tokens.refreshExpiresIn > 0) {
      input.refresh_expires_in = tokens.refreshExpiresIn;
    }
    enroll(input)
      .then((out) => {
        useAuthV2.getState().setSession({
          accessToken: out.access_token,
          casdoorSub: out.casdoor_sub,
          displayName: out.display_name,
          staff: out.staff,
          permissions: out.permissions ?? [],
          branchScope: out.branchScope,
          dataVisibility: out.dataVisibility,
          persona: out.persona,
        });
        markSessionSnapshotFresh();
        goHomeAfterLogin();
      })
      .catch((e) => {
        const err = e as AuthError;
        // QĐ 0412: nhân sự đã nghỉ — câu máy chủ đã đủ nghĩa, không kèm tiền tố.
        setError(err.code === STAFF_INACTIVE_CODE ? err.message : `Đăng ký thiết bị thất bại: ${err.message}`);
        setBusy(false);
      });
  };

  if (error) return <Center><p className="text-sm text-danger-text">{error}</p></Center>;
  if (!tokens) return <Center><p className="text-sm text-muted">Đang xử lý…</p></Center>;
  return (
    <Center>
      <div className="w-full max-w-sm rounded-lg border bg-surface p-8 shadow-sm">
        <h1 className="mb-1 text-2xl font-semibold">Đặt PIN</h1>
        <p className="mb-6 text-sm text-muted">
          Lần đầu đăng nhập trên thiết bị này. PIN sẽ dùng để mở khoá nhanh các lần sau.
        </p>
        <PinPad onSubmit={submitPin} busy={busy} />
      </div>
    </Center>
  );
}

function Center({ children }: { children: React.ReactNode }) {
  return <div className="flex min-h-screen items-center justify-center">{children}</div>;
}

// Local helper: the legacy `exchangeCodeForCasdoorToken` returns ONLY the
// access_token. We need both, so we call `casdoor.exchangeForAccessToken()`
// directly — the SDK's underlying response already contains refresh_token,
// the legacy helper just discards it. Cast keeps TS happy without modifying
// the SDK type.
async function exchangeCodeForCasdoorTokenWithRefresh(): Promise<{
  access_token: string;
  refresh_token: string;
  refresh_expires_in?: number;
}> {
  const r = await (casdoor as unknown as {
    exchangeForAccessToken: () => Promise<{ access_token: string; refresh_token: string; refresh_expires_in?: number }>;
  }).exchangeForAccessToken();
  if (!r.access_token || !r.refresh_token) {
    throw new Error("Casdoor response missing access_token or refresh_token");
  }
  const out: {
    access_token: string;
    refresh_token: string;
    refresh_expires_in?: number;
  } = {
    access_token: r.access_token,
    refresh_token: r.refresh_token,
  };
  if (typeof r.refresh_expires_in === "number" && r.refresh_expires_in > 0) {
    out.refresh_expires_in = r.refresh_expires_in;
  }
  return out;
}
